import { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroCanvas from './HeroCanvas';
import { useLang } from '../../../context/LanguageContext';

export default function HeroBanner() {
  const sectionRef = useRef<HTMLElement>(null);
  const bgImgRef = useRef<HTMLImageElement>(null);
  const { t } = useLang();

  const scrollDown = () => {
    const next = sectionRef.current?.nextElementSibling;
    next?.scrollIntoView({ behavior: 'smooth' });
  };

  // Parallax: move bg image at 30% of scroll speed (GPU-friendly with transform)
  useEffect(() => {
    const handleScroll = () => {
      if (!bgImgRef.current) return;
      const y = window.scrollY * 0.28;
      bgImgRef.current.style.transform = `translateY(${y}px)`;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full h-screen min-h-[680px] flex items-center justify-center overflow-hidden"
      style={{ background: '#eeeeee' }}
    >
      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-14px); }
        }
        @keyframes glowFade {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%       { opacity: 1;    transform: scale(1.18); }
        }
        @keyframes orbMove1 {
          0%,100% { transform: translate(0px, 0px) scale(1); }
          33%     { transform: translate(70px, -60px) scale(1.1); }
          66%     { transform: translate(-40px, 40px) scale(0.94); }
        }
        @keyframes orbMove2 {
          0%,100% { transform: translate(0px, 0px) scale(1); }
          40%     { transform: translate(-80px, 60px) scale(1.12); }
          70%     { transform: translate(50px, -40px) scale(0.92); }
        }
        @keyframes scanSlide {
          0%   { transform: translateY(-100%); opacity: 0.6; }
          80%  { opacity: 0.15; }
          100% { transform: translateY(10000%); opacity: 0; }
        }
        @keyframes scrollPulse {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50%      { transform: scale(1.6); opacity: 0; }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }
        @keyframes gridPulse {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 0.85; }
        }
        @keyframes bgZoomIn {
          0%   { transform: translateY(0) scale(1.10); opacity: 0; }
          100% { transform: translateY(0) scale(1); opacity: 0.65; }
        }
        @keyframes gridFadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes logoEnter {
          0%   { opacity: 0; transform: scale(0.35) rotate(-8deg); filter: blur(12px); }
          55%  { opacity: 1; transform: scale(1.12) rotate(2deg); filter: blur(0px); }
          75%  { transform: scale(0.96) rotate(-1deg); }
          100% { transform: scale(1) rotate(0deg); }
        }
        @keyframes eyebrowReveal {
          0%   { opacity: 0; transform: translateY(12px); letter-spacing: 0.05em; }
          100% { opacity: 1; transform: translateY(0); letter-spacing: 0.35em; }
        }
        @keyframes titleClipReveal {
          0%   { clip-path: inset(100% 0 0 0); transform: translateY(20px); opacity: 0.3; }
          100% { clip-path: inset(0% 0 0 0);   transform: translateY(0);    opacity: 1; }
        }
        @keyframes subtitleReveal {
          0%   { opacity: 0; transform: translateY(16px); filter: blur(6px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
        }
        @keyframes ctaSlideUp {
          0%   { opacity: 0; transform: translateY(28px) scale(0.94); }
          60%  { transform: translateY(-4px) scale(1.01); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes scrollReveal {
          0%   { opacity: 0; transform: translateX(-50%) translateY(16px); }
          100% { opacity: 1; transform: translateX(-50%) translateY(0); }
        }

        .hero-bg-enter {
          animation: bgZoomIn 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.1s both;
          will-change: transform, opacity;
        }
        .logo-enter {
          animation: logoEnter 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.8s both;
          will-change: transform, opacity, filter;
        }
        .logo-float {
          animation: logoFloat 5s ease-in-out 1.7s infinite;
          will-change: transform;
        }
        .eyebrow-reveal {
          animation: eyebrowReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) 1.2s both;
        }
        .title-line-1 {
          animation: titleClipReveal 0.75s cubic-bezier(0.22, 1, 0.36, 1) 1.45s both;
          display: inline-block;
        }
        .title-line-2 {
          animation: titleClipReveal 0.75s cubic-bezier(0.22, 1, 0.36, 1) 1.65s both;
          display: inline-block;
        }
        .subtitle-reveal {
          animation: subtitleReveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) 1.85s both;
        }
        .cta-1 {
          animation: ctaSlideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 2.05s both;
        }
        .cta-2 {
          animation: ctaSlideUp 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 2.2s both;
        }
        .scroll-reveal {
          animation: scrollReveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) 2.5s both;
        }
        .scroll-ring-pulse { animation: scrollPulse 2.2s ease-out infinite; }
        .scroll-arrow { animation: arrowBounce 1.8s ease-in-out infinite; }
        .hero-bg-parallax { will-change: transform; }
      `}</style>

      {/* ── Imagen de fondo con parallax y velo oscuro plano (sin degradados) ── */}
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        <img
          ref={bgImgRef}
          src="/imgs/background.webp"
          alt=""
          aria-hidden="true"
          className="hero-bg-enter hero-bg-parallax absolute inset-0 w-full object-cover object-center"
          style={{ top: '-10%', height: '120%', mixBlendMode: 'multiply' }}
          loading="eager"
        />
      </div>

      {/* ── Particle canvas ── */}
      <div className="absolute inset-0" style={{ zIndex: 2 }}>
        <HeroCanvas />
      </div>

      {/* ── Content ── */}
      <div className="relative flex flex-col items-center text-center px-6 w-full max-w-4xl mx-auto" style={{ zIndex: 3 }}>
        {/* Logo with glow + float */}
        <div className="logo-float mb-8 relative flex items-center justify-center">
          <img
            src="/imgs/logo.webp"
            alt="SpartechRD"
            className="logo-enter relative w-20 h-20 sm:w-28 sm:h-28 md:w-40 md:h-40 object-contain drop-shadow-2xl"
            fetchPriority="high"
            loading="eager"
          />
        </div>

        {/* Eyebrow */}
        <div className="eyebrow-reveal flex items-center gap-3 mb-4">
          <div className="h-px bg-red-500/70" style={{ width: '40px' }} />
          <span className="text-red-400 text-[10px] font-extrabold uppercase tracking-[0.35em]">
            {t('hero_eyebrow')}
          </span>
          <div className="h-px bg-red-500/70" style={{ width: '40px' }} />
        </div>

        {/* Title */}
        <h1 className="text-zinc-900 text-2xl sm:text-4xl md:text-6xl font-extrabold uppercase tracking-widest mb-4 leading-tight drop-shadow-2xl overflow-hidden">
          <span className="title-line-1 block">{t('hero_title1')}</span>
          <span className="title-line-2 block text-red-600">{t('hero_title2')}</span>
        </h1>

        <p className="subtitle-reveal text-zinc-600 text-xs sm:text-sm md:text-base mb-8 max-w-md leading-relaxed px-2">
          {t('hero_subtitle')}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center w-full sm:w-auto px-4 sm:px-0">
          <Link
            to="/products"
            className="cta-1 sp-btn sp-btn-solid w-full sm:w-auto uppercase tracking-widest"
          >
            {t('hero_cta1')}
          </Link>
          <a
            href="#cotizacion"
            onClick={(e) => { e.preventDefault(); document.querySelector('#cotizacion')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="cta-2 sp-btn sp-btn-red w-full sm:w-auto uppercase tracking-widest"
          >
            {t('hero_cta2')}
          </a>
        </div>
      </div>

      {/* ── Scroll button ── */}
      <button
        onClick={scrollDown}
        style={{ zIndex: 3 }}
        className="scroll-reveal absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
        aria-label="Scroll down"
      >
        <span className="text-zinc-600 group-hover:text-zinc-900 text-[9px] uppercase tracking-[0.45em] transition-colors duration-300">
          {t('hero_scroll')}
        </span>
        <div className="relative w-11 h-11 flex items-center justify-center">
          <div className="scroll-ring-pulse absolute inset-0 rounded-full border border-red-500/50" />
          <div className="absolute inset-0 rounded-full border border-zinc-300 group-hover:border-zinc-500 transition-colors duration-300" />
          <div className="absolute inset-[6px] rounded-full bg-zinc-900/5 group-hover:bg-red-600/10 transition-colors duration-300" />
          <i className="scroll-arrow ri-arrow-down-s-line text-zinc-600 group-hover:text-zinc-900 text-lg transition-colors duration-300 relative z-10" />
        </div>
      </button>
    </section>
  );
}
